import { useQuery } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useId, useState } from "react";
import { connectorsByIdentifier } from "../../../shared/connectors";
import { CHAT_WORKFLOWS_FLAG } from "../../../shared/featureFlagNames";
import { queries } from "../../../shared/queries";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { mobileMedia, useIsMobile } from "../../hooks/useIsMobile";
import { resolveProduct } from "../../lib/connector";
import { ConnectorImage } from "../ConnectorImage";
import { Icon } from "../Icon";
import { PressButton } from "../PressButton";
import { TabButton } from "../TabButton";
import { Text } from "../Text";
import {
  chatExampleCategories,
  type ChatExample,
  type ChatExampleCategory,
} from "./chatExampleCatalog";
import {
  pickChatExamples,
  plainPrompt,
  promptSegments,
} from "./chatExamplePrompts";

const SuggestionGrid = styled("div")`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 240px));
  grid-auto-rows: 56px;
  justify-content: center;
  gap: 8px;
  width: 100%;
  transition: opacity var(--motion-base) var(--ease-out);
  &[data-ready="false"] {
    opacity: 0;
  }
  ${mobileMedia} {
    grid-template-columns: 240px;
  }
  @container (max-width: 560px) {
    grid-template-columns: minmax(0, 320px);
  }
`;

const Suggestions = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
`;

const SuggestionTabs = styled("div")`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
  max-width: 100%;
`;

const SuggestionTab = styled(TabButton)`
  padding: 6px 10px;
  font-size: 12px;
  line-height: 16px;
`;

const SuggestionCard = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  text-align: left;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  cursor: pointer;
  &:hover {
    background:
      linear-gradient(var(--alpha4), var(--alpha4)), var(--surface-primary);
  }
`;

const SuggestionIcon = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
`;

const Logo = styled(ConnectorImage)`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  object-fit: contain;
`;

const SuggestionText = styled("div")`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  min-width: 0;
  font-size: 12px;
  line-height: 16px;
`;

export function ChatSuggestions({
  onPick,
  seed,
  prompts,
  pickExamples,
}: {
  onPick: (prompt: string) => void;
  seed: string;
  prompts?: readonly string[];
  pickExamples?: (
    connectorIdentifiers: readonly string[],
    count: number
  ) => ChatExample[];
}) {
  const [connectors, details] = useQuery(queries.connectorsIndex());
  const isMobile = useIsMobile();
  const [selectedCategory, setSelectedCategory] =
    useState<ChatExampleCategory>("tasks");
  const suggestionsId = useId();
  const chatWorkflows = useFeatureFlag(CHAT_WORKFLOWS_FLAG);
  const categories = chatWorkflows
    ? chatExampleCategories
    : chatExampleCategories.filter(
        ({ id }) => id !== "workflows" && id !== "apps"
      );
  const category =
    categories.find(({ id }) => id === selectedCategory)?.id ?? "tasks";
  const showTabs = !prompts && !pickExamples;

  // Fewer cards on mobile: six stacked single-column cards plus the header
  // rarely fit a phone viewport.
  const exampleCount = isMobile ? 4 : 6;
  const identifiers = [
    ...new Set(connectors.map((c) => resolveProduct(c) ?? c.identifier)),
  ];
  const ready =
    prompts != null || category === "monitoring" || details.type === "complete";
  const examples = prompts
    ? prompts
        .slice(0, exampleCount)
        .map((prompt) => ({ prompt, connectors: [] as string[] }))
    : !ready
      ? []
      : pickExamples
        ? pickExamples(identifiers, exampleCount)
        : pickChatExamples(identifiers, exampleCount, seed, category);

  return (
    <Suggestions>
      {showTabs && (
        <SuggestionTabs
          role="tablist"
          aria-label="Suggestion categories"
          onKeyDown={(event) => {
            const index = categories.findIndex(({ id }) => id === category);
            let nextIndex: number;
            switch (event.key) {
              case "ArrowRight":
                nextIndex = (index + 1) % categories.length;
                break;
              case "ArrowLeft":
                nextIndex = (index - 1 + categories.length) % categories.length;
                break;
              case "Home":
                nextIndex = 0;
                break;
              case "End":
                nextIndex = categories.length - 1;
                break;
              default:
                return;
            }
            event.preventDefault();
            setSelectedCategory(categories[nextIndex]!.id);
            event.currentTarget
              .querySelectorAll<HTMLButtonElement>('[role="tab"]')
              [nextIndex]?.focus();
          }}
        >
          {categories.map(({ id, label }) => (
            <SuggestionTab
              key={id}
              id={`${suggestionsId}-${id}`}
              role="tab"
              aria-selected={category === id}
              aria-controls={suggestionsId}
              tabIndex={category === id ? 0 : -1}
              className={category === id ? "active" : undefined}
              onPress={() => setSelectedCategory(id)}
            >
              {label}
            </SuggestionTab>
          ))}
        </SuggestionTabs>
      )}
      <SuggestionGrid
        data-ready={ready}
        id={suggestionsId}
        role={showTabs ? "tabpanel" : undefined}
        aria-labelledby={showTabs ? `${suggestionsId}-${category}` : undefined}
      >
        {examples.map((example) => {
          const lead = example.connectors[0];
          const logo = lead ? connectorsByIdentifier[lead]?.logo : undefined;
          return (
            <SuggestionCard
              key={example.prompt}
              data-no-pill
              onPress={() => onPick(plainPrompt(example.prompt))}
            >
              <SuggestionIcon>
                {logo ? (
                  <Logo src={logo} alt="" />
                ) : (
                  <Icon
                    name={
                      prompts || category === "monitoring"
                        ? "scan-heart"
                        : "api-key-custom"
                    }
                    size={16}
                    color="var(--text-primary)"
                  />
                )}
              </SuggestionIcon>
              <SuggestionText>
                {promptSegments(example.prompt).map((segment) => (
                  <Text
                    key={segment.text}
                    variant={segment.bold ? "text-regular-md" : "text-regular"}
                    color="var(--text-primary)"
                  >
                    {segment.text}
                  </Text>
                ))}
              </SuggestionText>
            </SuggestionCard>
          );
        })}
      </SuggestionGrid>
    </Suggestions>
  );
}
